import { ExternalLink } from 'lucide-react';

import { ScrollReveal, SplitReveal } from '@/components/motion';
import {
  ISO_CAMPUS_MANAGER,
  ISO_COMMS_NOTE,
  ISO_CONTACT,
  ISO_LEDE,
  ISO_PARTNERS,
  ISO_SERVICES,
} from '@/data/current-students';

/** Shared underline-on-hover idiom for navy contact links (email / phone). */
const CONTACT_LINK =
  'font-medium text-brand-navy underline decoration-2 decoration-transparent underline-offset-4 transition-colors duration-300 hover:decoration-brand-gold';

/** Typographic initials for a monogram avatar — honorifics stripped. */
function initialsOf(name: string): string {
  return name
    .replace(/\b(dr|prof|mr|mrs|ms|jr)\.?/gi, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * InternationalOffice (/current-students — section 1) — "Support from the
 * moment you arrive."
 *
 * The International Students Office desk: ISO_LEDE frames the office, then
 * the nine ISO_SERVICES render as a ScrollReveal-staggered grid of plain
 * hover-lift cards (icon in a navy chip + name + description). Below, a
 * two-part support block pairs (a) a contact panel — the ISO_CONTACT rows
 * (tel:/mailto: styled navy-underline links, the OpenSIS https link treated
 * as an external link, plain text for the rest), a monogram card for
 * ISO_CAMPUS_MANAGER with a mailto link, and ISO_COMMS_NOTE as a small note
 * — against (b) the ISO_PARTNERS as small outbound link cards.
 *
 * Motion: SplitReveal headline, ScrollReveal child-staggers throughout.
 * Reduced-motion safe; every institutional fact comes from
 * '@/data/current-students', nothing is invented.
 */
export function InternationalOffice() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            International Students Office
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Support from the moment you arrive.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              {ISO_LEDE}
            </p>
          </ScrollReveal>
        </header>

        {/* ── Services grid ────────────────────────────────────────────── */}
        <ScrollReveal
          stagger={0.05}
          className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
        >
          {ISO_SERVICES.map((service) => (
            <div
              key={service.name}
              className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                <service.Icon aria-hidden className="size-6" />
              </div>
              <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                {service.name}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {service.description}
              </p>
            </div>
          ))}
        </ScrollReveal>

        {/* ── Support block: contact panel · partner links ───────────────── */}
        <div className="mt-16 grid grid-cols-1 gap-6 border-t border-border pt-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-8">
          {/* ── Contact panel ────────────────────────────────────────────── */}
          <div>
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              Get in touch
            </p>

            <ScrollReveal stagger={0.06} className="mt-6 flex flex-col gap-4">
              {ISO_CONTACT.map((row) => {
                const isExternal = row.href?.startsWith('http');
                return (
                  <div
                    key={row.label}
                    className="flex items-start gap-4 rounded-xl border border-border bg-muted/30 p-5"
                  >
                    <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                      <row.Icon aria-hidden className="size-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                        {row.label}
                      </p>
                      {row.href ? (
                        isExternal ? (
                          <a
                            href={row.href}
                            target="_blank"
                            rel="noreferrer"
                            data-cursor
                            className={`mt-1 block text-sm break-words ${CONTACT_LINK}`}
                          >
                            {row.value}
                          </a>
                        ) : (
                          <a href={row.href} className={`mt-1 block text-sm break-words ${CONTACT_LINK}`}>
                            {row.value}
                          </a>
                        )
                      ) : (
                        <p className="mt-1 text-sm leading-relaxed text-foreground">{row.value}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </ScrollReveal>

            <ScrollReveal from="up" delay={0.1}>
              <div className="mt-4 flex items-start gap-4 rounded-xl border border-border bg-background p-5">
                <span
                  aria-hidden
                  className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 font-display text-sm font-semibold tracking-wide text-brand-navy"
                >
                  {initialsOf(ISO_CAMPUS_MANAGER.name)}
                </span>
                <div className="min-w-0">
                  <p className="font-display text-base leading-tight font-semibold text-foreground">
                    {ISO_CAMPUS_MANAGER.name}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {ISO_CAMPUS_MANAGER.role}
                  </p>
                  <a
                    href={`mailto:${ISO_CAMPUS_MANAGER.email}`}
                    className={`mt-1.5 block text-sm break-words ${CONTACT_LINK}`}
                  >
                    {ISO_CAMPUS_MANAGER.email}
                  </a>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal from="up" delay={0.15}>
              <p className="mt-5 text-xs leading-relaxed text-muted-foreground">{ISO_COMMS_NOTE}</p>
            </ScrollReveal>
          </div>

          {/* ── Partner links ─────────────────────────────────────────────── */}
          <div>
            <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
              Partner resources
            </p>
            <ScrollReveal stagger={0.08} className="mt-6 flex flex-col gap-4">
              {ISO_PARTNERS.map((partner) => (
                <a
                  key={partner.name}
                  href={partner.href}
                  target="_blank"
                  rel="noreferrer"
                  data-cursor
                  className="flex items-start justify-between gap-4 rounded-xl border border-border bg-background p-5 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                >
                  <div className="min-w-0">
                    <p className="font-display text-base leading-tight font-semibold text-foreground">
                      {partner.name}
                    </p>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                      {partner.detail}
                    </p>
                  </div>
                  <ExternalLink aria-hidden className="mt-1 size-4 shrink-0 text-brand-gold" />
                </a>
              ))}
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>
  );
}
